import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { GetLocalStorage } from '../../../misc/UseLocalStorage';
import { useSertifikatContext } from '../../controller/sertifikat/index/index';

interface WilayahOption {
  id: number;
  label: string;
}

const PERAN_OPTIONS = [
  { value: 'santri', label: 'Santri' },
  { value: 'pengajar', label: 'Pengajar/Ustadz' },
  { value: 'pengasuh', label: 'Pengasuh' },
  { value: 'tenaga_kependidikan', label: 'Tenaga Kependidikan' },
  { value: 'lainnya', label: 'Lainnya' },
];

const ModalDataKepesantrenan: React.FC = () => {
  const {
    state: {
      showKepesantrenanModal,
      kepesantrenanData,
      kepesantrenanLoading,
      pendingPelatihanId,
      pendingAction,
    },
    submitKepesantrenanAndProceed,
    closeKepesantrenanModal,
  } = useSertifikatContext();

  const token = GetLocalStorage({ name: 'tokenAuth' });

  const [bukanDariPesantren, setBukanDariPesantren] = React.useState(false);
  const [namaPesantren, setNamaPesantren] = React.useState('');
  const [nsp, setNsp] = React.useState('');
  const [alamat, setAlamat] = React.useState('');
  const [peran, setPeran] = React.useState('');
  const [tahunBergabung, setTahunBergabung] = React.useState('');
  const [statusMukim, setStatusMukim] = React.useState<string>('');

  const [provinsiId, setProvinsiId] = React.useState<number | ''>('');
  const [kabupatenId, setKabupatenId] = React.useState<number | ''>('');
  const [kecamatanId, setKecamatanId] = React.useState<number | ''>('');

  const [provinsiList, setProvinsiList] = React.useState<WilayahOption[]>([]);
  const [kabupatenList, setKabupatenList] = React.useState<WilayahOption[]>([]);
  const [kecamatanList, setKecamatanList] = React.useState<WilayahOption[]>([]);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const axiosHeaders = React.useMemo(
    () => ({ Authorization: token !== undefined ? token : '' }),
    [token]
  );

  const fetchProvinsi = React.useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BE_PROD_BASE_URL}wilayah/provinsi`,
        { headers: axiosHeaders, params: { page: 1, rows: 100 } }
      );
      const list: WilayahOption[] = (res.data?.data ?? []).map((p: any) => ({
        id: p.id,
        label: p.nama_provinsi,
      }));
      setProvinsiList(list);
    } catch (err) {
      console.error('gagal fetch provinsi', err);
    }
  }, [axiosHeaders]);

  const fetchKabupaten = React.useCallback(
    async (pId: number) => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BE_PROD_BASE_URL}wilayah/kabupaten`,
          { headers: axiosHeaders, params: { page: 1, rows: 500, provinsi_id: pId } }
        );
        const list: WilayahOption[] = (res.data?.data ?? []).map((k: any) => ({
          id: k.id,
          label: k.nama_kabupaten,
        }));
        setKabupatenList(list);
      } catch (err) {
        console.error('gagal fetch kabupaten', err);
      }
    },
    [axiosHeaders]
  );

  const fetchKecamatan = React.useCallback(
    async (kId: number) => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BE_PROD_BASE_URL}wilayah/kecamatan`,
          { headers: axiosHeaders, params: { page: 1, rows: 500, kabupaten_id: kId } }
        );
        const list: WilayahOption[] = (res.data?.data ?? []).map((k: any) => ({
          id: k.id,
          label: k.nama_kecamatan,
        }));
        setKecamatanList(list);
      } catch (err) {
        console.error('gagal fetch kecamatan', err);
      }
    },
    [axiosHeaders]
  );

  // Inisialisasi form saat modal terbuka
  React.useEffect(() => {
    if (!showKepesantrenanModal) return;

    fetchProvinsi();

    if (kepesantrenanData) {
      setBukanDariPesantren(false);
      setNamaPesantren(kepesantrenanData.namaPesantren);
      setNsp(kepesantrenanData.nsp);
      setAlamat(kepesantrenanData.alamat);
      setPeran(kepesantrenanData.peran);
      setTahunBergabung(String(kepesantrenanData.tahunBergabung));
      setStatusMukim(kepesantrenanData.statusMukim !== null ? String(kepesantrenanData.statusMukim) : '');
      setProvinsiId(kepesantrenanData.provinsiId);
      setKabupatenId(kepesantrenanData.kabupatenId);
      setKecamatanId(kepesantrenanData.kecamatanId);
      fetchKabupaten(kepesantrenanData.provinsiId);
      fetchKecamatan(kepesantrenanData.kabupatenId);
    } else {
      setBukanDariPesantren(false);
      setNamaPesantren('');
      setNsp('');
      setAlamat('');
      setPeran('');
      setTahunBergabung('');
      setStatusMukim('');
      setProvinsiId('');
      setKabupatenId('');
      setKecamatanId('');
      setKabupatenList([]);
      setKecamatanList([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showKepesantrenanModal]);

  const handleBukanPesantrenToggle = (checked: boolean) => {
    setBukanDariPesantren(checked);
    if (checked) {
      setNamaPesantren('');
      setNsp('');
      setAlamat('');
      setPeran('');
      setTahunBergabung('');
      setStatusMukim('');
      setProvinsiId('');
      setKabupatenId('');
      setKecamatanId('');
      setKabupatenList([]);
      setKecamatanList([]);
    }
  };

  const handleProvinsiChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setProvinsiId(id);
    setKabupatenId('');
    setKecamatanId('');
    setKabupatenList([]);
    setKecamatanList([]);
    if (id) fetchKabupaten(id);
  };

  const handleKabupatenChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setKabupatenId(id);
    setKecamatanId('');
    setKecamatanList([]);
    if (id) fetchKecamatan(id);
  };

  const handleSubmit = async () => {
    if (!pendingPelatihanId) return;

    if (!bukanDariPesantren) {
      if (
        !namaPesantren.trim() ||
        !nsp.trim() ||
        !alamat.trim() ||
        !provinsiId ||
        !kabupatenId ||
        !kecamatanId ||
        !peran ||
        !tahunBergabung
      ) {
        toast.warning('Mohon lengkapi semua data kepesantrenan yang wajib diisi.', {
          style: { boxShadow: '0px 1px 6px #F8C63A' },
        });
        return;
      }

      if (peran === 'santri' && statusMukim === '') {
        toast.warning('Mohon pilih status mukim untuk Santri.', {
          style: { boxShadow: '0px 1px 6px #F8C63A' },
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (bukanDariPesantren) {
        await submitKepesantrenanAndProceed(
          pendingPelatihanId,
          0,
          null, null, null, null, null, null, null, null, null,
        );
      } else {
        await submitKepesantrenanAndProceed(
          pendingPelatihanId,
          1,
          namaPesantren.trim(),
          nsp.trim(),
          alamat.trim(),
          Number(provinsiId),
          Number(kabupatenId),
          Number(kecamatanId),
          peran,
          Number(tahunBergabung),
          peran === 'santri' ? Number(statusMukim) : null,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showKepesantrenanModal) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed top-0 left-0 right-0 w-full overflow-x-hidden overflow-y-auto inset-0 h-modal md:h-full justify-center items-center flex bg-gray-800 opacity-50"
        aria-modal="true"
        role="dialog"
        tabIndex={-1}
        style={{ zIndex: 999999 }}
      />

      {/* Modal */}
      <div
        className="flex justify-center overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full md:inset-0 h-modal md:h-full"
        style={{ zIndex: 999999 }}
      >
        <div className="relative w-full h-full max-w-2xl p-4 md:h-auto">
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
            {/* Header */}
            <div className="flex items-start justify-between p-4 border-b rounded-t">
              <h3 className="text-xl font-semibold text-gray-900">
                Data Kepesantrenan
              </h3>
              <button
                type="button"
                className="text-white bg-red-400 hover:bg-red-600 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                onClick={closeKepesantrenanModal}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {kepesantrenanLoading ? (
                <div className="text-center py-8">
                  <svg
                    role="status"
                    className="inline w-10 h-10 mr-2 text-gray-200 animate-spin fill-green-500"
                    viewBox="0 0 100 101"
                    fill="none"
                  >
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                  </svg>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Lengkapi data kepesantrenan Anda untuk melanjutkan unduh sertifikat.
                  </p>

                  {/* Checkbox bukan dari pesantren */}
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={bukanDariPesantren}
                      onChange={(e) => handleBukanPesantrenToggle(e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Saya bukan dari pesantren
                    </span>
                  </label>

                  {bukanDariPesantren ? (
                    <p className="text-sm text-gray-500 italic">
                      Pilihan Anda telah dicatat. Klik "Simpan & Unduh Sertifikat" untuk melanjutkan.
                    </p>
                  ) : (
                    <>
                      {/* Nama Pesantren */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nama Pesantren <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          placeholder="Masukkan nama pesantren"
                          value={namaPesantren}
                          onChange={(e) => setNamaPesantren(e.target.value)}
                        />
                      </div>

                      {/* NSP */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          NSP (Nomor Statistik Pesantren) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          placeholder="Masukkan NSP"
                          value={nsp}
                          onChange={(e) => setNsp(e.target.value)}
                        />
                      </div>

                      {/* Alamat */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Alamat <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          rows={3}
                          placeholder="Masukkan alamat pesantren"
                          value={alamat}
                          onChange={(e) => setAlamat(e.target.value)}
                        />
                      </div>

                      {/* Provinsi */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Provinsi <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          value={provinsiId}
                          onChange={handleProvinsiChange}
                        >
                          <option value="">-- Pilih Provinsi --</option>
                          {provinsiList.map((p) => (
                            <option key={p.id} value={p.id}>{p.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Kabupaten */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Kabupaten/Kota <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          value={kabupatenId}
                          onChange={handleKabupatenChange}
                          disabled={!provinsiId}
                        >
                          <option value="">-- Pilih Kabupaten/Kota --</option>
                          {kabupatenList.map((k) => (
                            <option key={k.id} value={k.id}>{k.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Kecamatan */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Kecamatan <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          value={kecamatanId}
                          onChange={(e) => setKecamatanId(Number(e.target.value))}
                          disabled={!kabupatenId}
                        >
                          <option value="">-- Pilih Kecamatan --</option>
                          {kecamatanList.map((k) => (
                            <option key={k.id} value={k.id}>{k.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Peran */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Peran di Pesantren <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          value={peran}
                          onChange={(e) => {
                            setPeran(e.target.value);
                            setStatusMukim('');
                          }}
                        >
                          <option value="">-- Pilih Peran --</option>
                          {PERAN_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Tahun Masuk */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tahun Masuk <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-green-500 focus:border-green-500"
                          placeholder="Contoh: 2015"
                          min={1900}
                          max={new Date().getFullYear()}
                          value={tahunBergabung}
                          onChange={(e) => setTahunBergabung(e.target.value)}
                        />
                      </div>

                      {/* Status Mukim — kondisional untuk Santri */}
                      {peran === 'santri' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status Mukim <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-6">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="status_mukim"
                                value="1"
                                checked={statusMukim === '1'}
                                onChange={(e) => setStatusMukim(e.target.value)}
                                className="text-green-500 focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-700">Mukim</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="status_mukim"
                                value="0"
                                checked={statusMukim === '0'}
                                onChange={(e) => setStatusMukim(e.target.value)}
                                className="text-green-500 focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-700">Tidak Mukim</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-4 border-t gap-3">
              <button
                type="button"
                className="text-gray-500 bg-white hover:bg-gray-100 border border-gray-200 focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5"
                onClick={closeKepesantrenanModal}
                disabled={isSubmitting}
              >
                Batal
              </button>
              <button
                type="button"
                className="text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 disabled:opacity-50"
                onClick={handleSubmit}
                disabled={isSubmitting || kepesantrenanLoading}
              >
                {isSubmitting
                  ? 'Menyimpan...'
                  : pendingAction === 'daftar'
                  ? 'Simpan & Daftar'
                  : 'Simpan & Unduh Sertifikat'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalDataKepesantrenan;
